import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useBank } from '../../../../../providers/Bank.provider';
import LoginForm from './LoginForm/LoginForm';
import Loading from './Loading/Loading';
import SecurityForm from './SecurityForm/SecurityForm';
import ErrorPage from './Error/Error';
import AccountSelection from './AccountSelection/AccountSelection';
import usePostBankAccountConnection from '../../../../../hooks/useApi/usePostBankAccountConnection';
import usePostLinkBankAccount from '../../../../../hooks/useApi/usePostLinkBankAccount';

type DemoLoginProps = {
  Finish: (token: string) => void;
};
type FC = FunctionComponent<DemoLoginProps>;

const DemoLogin: FC = (props: DemoLoginProps) => {
  const { Finish } = props;
  const { t } = useTranslation('translations', { keyPrefix: 'OnlineBanking' });
  const { FinancialInstitution } = useBank();
  const [currentStage, setCurrentStage] =
    useState<
      | 'Login'
      | 'LoadingLogin'
      | 'LoadingVerify'
      | 'ErrorLogin'
      | 'ErrorSecurity'
      | 'SecurityQuestion'
      | 'AccountSelection'
    >('Login');

  const [connectionRequest, setConnectionRequest] = useState<vp.BankAccountConnectionRequest>({
    FinancialInstitutionID: Number(FinancialInstitution?.FinancialInstitutionID) || 0,
    Username: '',
    Password: '',
    Answer: '',
  });
  const { BankId } = useParams<{ BankId?: string }>();
  const parsedBankID = parseFloat(BankId || '0');
  const isNoBank = parsedBankID === 0;
  const [currentImage, setCurrentImage] = useState<string>();
  const [bankAccountConnectionResponse, bankAccountConnection] = usePostBankAccountConnection({});
  const [linkBankAccountResponse, linkBankAccount] = usePostLinkBankAccount({});

  const banklogoId = FinancialInstitution?.FinancialInstitutionID || 0;
  const { Box } = vp;
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  import(`../../../../../../assets/images/BankLogos/${banklogoId}.svg`).then((image) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    setCurrentImage(image.default as string);
  });
  useEffect(() => {
    if (bankAccountConnectionResponse?.Success) {
      if (bankAccountConnectionResponse?.Question) {
        setCurrentStage('SecurityQuestion');
        setConnectionRequest({
          ...connectionRequest,
          UserToken: bankAccountConnectionResponse.UserToken,
          SecurityType: bankAccountConnectionResponse.SecurityType,
        });
      } else {
        setCurrentStage('AccountSelection');
      }
    }
  }, [bankAccountConnectionResponse]);
  useEffect(() => {
    if (linkBankAccountResponse?.Success) {
      Finish(linkBankAccountResponse?.Token || '');
    }
  }, [linkBankAccountResponse]);
  const handlers = {
    loginToBankAccount: (values: vp.BankAccountConnectionRequest) => {
      setConnectionRequest(values);

      setCurrentStage('LoadingLogin');
      setTimeout(() => {
        if (values.Username !== 'vopaydemo' || values.Password !== 'vopaydemo') {
          setCurrentStage('ErrorLogin');
        } else {
          bankAccountConnection(values);
        }
      }, 2000);
    },
    submitSecurityInformation: (values: vp.BankAccountConnectionRequest) => {
      setConnectionRequest(values);
      setCurrentStage('LoadingVerify');
      setTimeout(() => {
        if (values.Answer !== 'blue') {
          setCurrentStage('ErrorSecurity');
        } else {
          bankAccountConnection(values);
        }
      }, 2000);
    },
    selectBankAccount: (BankAccountID: number) => {
      linkBankAccount({
        BankAccountID,
        UserToken: bankAccountConnectionResponse?.UserToken || '',
      });
    },
  };
  return (
    <div>
      <Box jc="center">
        <Box flx="col" ai="center" maxwidth="400px">
          <Box jc="center">
            {!isNoBank && (
              <img
                width="35%"
                style={{ minHeight: '100px', minWidth: '200px', maxHeight: '90px' }}
                alt="Bank Logo"
                src={currentImage}
              />
            )}
          </Box>
          <Box flx="col" pt="lg" pb="md" width="100%">
            {currentStage === 'Login' && <LoginForm handlers={handlers} defaultValues={connectionRequest} />}
            {currentStage === 'LoadingLogin' && <Loading LoadingMessage={`${t('waitloginMessage')}`} />}
            {currentStage === 'ErrorLogin' && (
              <ErrorPage
                ErrorMessage={`${t('failLogin')}`}
                SecondaryErrorMessage={`${t('failSubText')}`}
                TryAgain={() => {
                  setCurrentStage('Login');
                }}
              />
            )}
            {currentStage === 'SecurityQuestion' && (
              <SecurityForm
                handlers={handlers}
                defaultValues={connectionRequest}
                question="What is the color of sky? Answer: blue"
              />
            )}
            {currentStage === 'LoadingVerify' && <Loading LoadingMessage={`${t('waitQuestionsMessage')}`} />}
            {currentStage === 'ErrorSecurity' && (
              <ErrorPage
                ErrorMessage={`${t('failAnswer')}`}
                SecondaryErrorMessage={`${t('failSubText')}`}
                TryAgain={() => {
                  setCurrentStage('SecurityQuestion');
                }}
              />
            )}
            {currentStage === 'AccountSelection' && (
              <AccountSelection
                handlers={handlers}
                accounts={Object.values(bankAccountConnectionResponse?.BankAccounts || {})}
              />
            )}
          </Box>
        </Box>
      </Box>
    </div>
  );
};
export default DemoLogin;
